import React, { useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  Breadcrumbs,
  Link,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Outlet, useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import { Image } from "../../components/design-system";
import logo from "../../assets/img/logo.png";
import { useAuth } from "../../hooks/useAuth.hook";
import { useWallet } from "../../hooks/useWallet.hook";
import { customerProtectedRoutes } from "../../routes/customer-protected.routes";
import { commonProtectedRoutes } from "../../routes/common-protected.routes";
import { adminProtectedRoutes } from "../../routes/admin-protected.routes";

const LoggedInLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { points } = useWallet();
  const navigate = useNavigate();


  const matches600 = useMediaQuery("(min-width:600px)");
  const [sidebarOpen, setSidebarOpen] = useState(matches600);
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const userLogout = () => {
    logout();
    navigate('/login');
  }

  return (
    <Box display="flex" height="100vh" bgcolor="#FFFFFF">
      {/* Sidebar */}
      <Drawer
        variant={matches600 ? "persistent" : "temporary"}
        open={sidebarOpen}
        onClose={toggleSidebar}
        sx={{
          width: sidebarOpen && matches600 ? 250 : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 250,
            boxSizing: "border-box",
            position: "relative",
            bgcolor: "#FFFFFF",
            color: "#333",
            padding: "1rem",
            zIndex: 999
          },
        }}
      >
        <Box display="flex" flexDirection="column" height="100vh">
          <IconButton
            onClick={toggleSidebar}
            sx={{ alignSelf: "flex-end", color: "#333" }}
          >
            <CloseIcon />
          </IconButton>
          <Image
            src={logo}
            style={{ marginBottom: "4rem" }}
            alt="Mariana Ramos Advocacia"
            objectFit="contain"
            maxHeight={60}
          />
          <Typography variant="h6" mb={2} fontWeight={700} color="#333">
            Menu
          </Typography>

          {commonProtectedRoutes.map((route, index) => (
            <Typography
              key={index}
              variant="body1"
              mb={2}
              sx={{
                textDecoration: "none",
                cursor: "pointer",
                color: "#555",
                "&:hover": { color: "#000" },
              }}
              component={RouterLink}
              to={route.path}
              onClick={() => {
                if (!matches600) toggleSidebar(); // Close sidebar only on mobile
              }}
            >
              {route.label ?? route.path.slice(1)}
            </Typography>
          ))}

          {user?.roles.includes("admin") && (
            <>
              {adminProtectedRoutes.map((route, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  mb={2}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    color: "#555",
                    "&:hover": { color: "#000" },
                  }}
                  component={RouterLink}
                  to={route.path}
                  onClick={() => {
                    if (!matches600) toggleSidebar(); // Close sidebar only on mobile
                  }}
                >
                  {route.label ?? route.path.slice(1)}
                </Typography>
              ))}
            </>
          )}

          {user?.roles.includes("customer") && (
            <>
              {customerProtectedRoutes.map((route, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  mb={2}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    color: "#555",
                    "&:hover": { color: "#000" },
                  }}
                  component={RouterLink}
                  to={route.path}
                  onClick={() => {
                    if (!matches600) toggleSidebar(); // Close sidebar only on mobile
                  }}
                >
                  {route.label ?? route.path.slice(1)}
                </Typography>
              ))}
            </>
          )}
          
        </Box>
      </Drawer>

      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        ml={sidebarOpen && matches600 ? "0px" : "0"}
        style={{ transition: "margin 0.3s ease" }}
      >
        {/* Navbar */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent={"space-between"}
          p={2}
          bgcolor="#FFF6F2"
          color="#333"
        >
          <IconButton onClick={toggleSidebar} sx={{ color: "#333" }}>
            <MenuIcon />
          </IconButton>
          <Box display="flex" alignItems="center">
            <Avatar sx={{ bgcolor: "#D89274", width: 40, height: 40, mr: 2 }}>
              U
            </Avatar>
            <Box textAlign="left">
              <Typography
                variant="body1"
                fontSize={matches600 ? "1rem" : ".875rem"}
                fontWeight={700}
              >
                {user?.name}   
              </Typography>
              {points && points >= 0 && 
                <Typography
                    variant="body2"
                    fontSize={matches600 ? ".875rem" : ".75rem"}
                    fontWeight={400}
                    color="#555"
                >
                    Você possui <b>{points} pontos</b>
                </Typography>
              }

              <Typography
                  variant="body2"
                  fontSize={matches600 ? ".875rem" : ".75rem"}
                  fontWeight={400}
                  color="#555"
                  onClick={userLogout}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
              >
                  Sair
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Breadcrumbs */}
        {/* <Box p={2}>
          <Breadcrumbs aria-label="breadcrumb">
            {location.pathname
              .split("/")
              .filter(Boolean)
              .map((path, index, arr) => (
                <Link
                  key={index}
                  color={index === arr.length - 1 ? "textPrimary" : "inherit"}
                  component={RouterLink}
                  to={`/${arr.slice(0, index + 1).join("/")}`}
                >
                  {path.charAt(0).toUpperCase() + path.slice(1)}
                </Link>
              ))}
          </Breadcrumbs>
        </Box> */}

        {/* Content Area */}
        <Box p={3} flexGrow={1}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export { LoggedInLayout };
